import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isoDate, monthName } from 'design/format';

/** The period selector supports a single month, a whole year, or the current year to date. */
export type Period =
  | { kind: 'month'; y: number; mo: number }
  | { kind: 'year'; y: number }
  | { kind: 'ytd'; y: number };

export interface DateRange {
  start: string;
  end: string;
}

/** The transactions list's sortable columns. */
export type SortKey = 'date' | 'merchant' | 'category' | 'account' | 'amount';
/**
 * A column, in its first-click order (newest, A–Z, highest), or `:rev` for the
 * reverse. One string so it travels through `openTransactions` unchanged.
 */
export type SortMode = SortKey | `${SortKey}:rev`;

const THEME_KEY = 'aif.st.theme';
const SIDEBAR_KEY = 'aif.st.sidebar';
const ACCENT_KEY = 'aif.st.accent';
const AI_KEY = 'aif.st.ai';

/**
 * The accent palette, as the profile menu offers it.
 *
 * The colours themselves live in `statement.css` under `[data-accent]`; what
 * is here is the list, its order, and the dot each row shows: the light
 * accent, except Forest's, which is the page's ink and so has to follow the
 * theme to stay visible on a dark menu.
 */
export const ACCENTS = [
  { key: 'original', label: 'Original', dot: '#4E6136' },
  { key: 'pink', label: 'Pink', dot: '#7A3E5E' },
  { key: 'teal', label: 'Teal', dot: '#2E5A52' },
  { key: 'violet', label: 'Violet', dot: '#3E5878' },
  { key: 'amber', label: 'Amber', dot: '#8A661A' },
  { key: 'forest', label: 'Forest', dot: 'var(--st-ink)' },
] as const;

export type Accent = (typeof ACCENTS)[number]['key'];

const DEFAULT_ACCENT: Accent = 'pink';

export function currentMonthPeriod(): Period {
  const now = new Date();
  return { kind: 'month', y: now.getFullYear(), mo: now.getMonth() };
}

/** Inclusive `YYYY-MM-DD` bounds for a period. `ytd` ends today. */
export function periodRange(p: Period): DateRange {
  if (p.kind === 'month') {
    return {
      start: isoDate(new Date(p.y, p.mo, 1)),
      end: isoDate(new Date(p.y, p.mo + 1, 0)),
    };
  }
  if (p.kind === 'year') {
    return { start: isoDate(new Date(p.y, 0, 1)), end: isoDate(new Date(p.y, 11, 31)) };
  }
  return { start: isoDate(new Date(p.y, 0, 1)), end: isoDate(new Date()) };
}

/** The period immediately before `p`, used for every "vs last month/year" comparison. */
export function previousPeriod(p: Period): Period {
  if (p.kind === 'month') {
    const mo = p.mo - 1;
    return mo < 0 ? { kind: 'month', y: p.y - 1, mo: 11 } : { kind: 'month', y: p.y, mo };
  }
  return { kind: 'year', y: p.y - 1 };
}

export function periodLabel(p: Period): string {
  if (p.kind === 'ytd') return `${p.y} to date`;
  if (p.kind === 'year') return String(p.y);
  return `${monthName(p.mo)} ${p.y}`;
}

/** Short form used inline in sentences ("Spent in July"). */
export function periodShort(p: Period): string {
  if (p.kind === 'month') return monthName(p.mo);
  return periodLabel(p);
}

export function periodsEqual(a: Period, b: Period): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'month' && b.kind === 'month') return a.y === b.y && a.mo === b.mo;
  return a.y === b.y;
}

/** True when `p` includes today, which disables the "next period" arrow. */
export function periodAtPresent(p: Period): boolean {
  const now = new Date();
  if (p.kind === 'ytd' || p.kind === 'year') return p.y >= now.getFullYear();
  return p.y > now.getFullYear() || (p.y === now.getFullYear() && p.mo >= now.getMonth());
}

export function stepPeriod(p: Period, delta: number): Period {
  const now = new Date();
  if (p.kind === 'month') {
    let mo = p.mo + delta;
    let y = p.y;
    while (mo > 11) { mo -= 12; y += 1; }
    while (mo < 0) { mo += 12; y -= 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && mo > now.getMonth())) return p;
    return { kind: 'month', y, mo };
  }
  const y = p.y + delta;
  if (y > now.getFullYear()) return p;
  return y === now.getFullYear() ? { kind: 'ytd', y } : { kind: 'year', y };
}

/**
 * Every filter the header's Reset pill is responsible for clearing. Kept in one
 * place so Reset, the Overview→Transactions link and the pages themselves can
 * never drift out of sync.
 */
interface Filters {
  /**
   * `null` means every account; otherwise a whitelist of account ids. Institution
   * scope is derived from this list — the scope filter selects a whole
   * institution by adding every one of its accounts.
   */
  accountFilter: string[] | null;
  /**
   * The same idea for Wealth, kept in its own field on purpose. That page
   * scopes to investment accounts only, and a selection made there would
   * otherwise narrow Transactions and Spending to the brokerage — hiding every
   * cash transaction the user has, with the control that did it on another
   * page. The two scopes never see each other.
   */
  wealthAccountFilter: string[] | null;
  /** Category selected on Spending, which also drives its detail panel. */
  spendingCategory: string | null;
  search: string;
  /**
   * One merchant's normalised identity, as `groupMerchants` keys it.
   *
   * Separate from `search` because a merchant is an identity, not a word: the
   * descriptors in a group need share no text a search box could be given — a
   * payee whose rows each carry their own reference number has no string that
   * matches all of them and only them. Spending's merchant rows set this.
   */
  merchant: string | null;
  /**
   * Categories the transactions list is narrowed to. Empty means every
   * category, and a selection may cross parents — picking Dining and Salary &
   * Wages together is a legitimate question to ask.
   */
  txnCats: string[];
  /** Tags the list is narrowed to. A row is kept if it carries *any* of them. */
  txnTags: string[];
  /**
   * Account *types* the list is narrowed to, by their display label
   * ("Checking", "Credit cards"). Distinct from `accountFilter`, which names
   * individual accounts: this asks "everything of this kind", which survives
   * connecting another card.
   */
  txnAcctTypes: string[];
  /** Narrows to recurring payments. There is no "not recurring" state. */
  recurring: boolean;
  /**
   * Narrows to rows nobody has confirmed the category of.
   *
   * One-sided like `recurring`: "show me what still needs checking" is the
   * question people have, and the inverse — every row already verified — is
   * one nobody asks a filter for.
   */
  unverified: boolean;
  /** `YYYY-MM-DD` (heatmap day) or `YYYY-MM` (heatmap month). */
  day: string | null;
  sort: SortMode;
  /** Extra months loaded by "Show <previous month>". */
  monthsBack: number;
}

const EMPTY_FILTERS: Filters = {
  accountFilter: null,
  wealthAccountFilter: null,
  spendingCategory: null,
  search: '',
  merchant: null,
  txnCats: [],
  txnTags: [],
  txnAcctTypes: [],
  recurring: false,
  unverified: false,
  day: null,
  sort: 'date',
  monthsBack: 0,
};

/** Options the Overview links use to open Transactions pre-filtered. */
export interface OpenTransactionsOptions {
  period?: Period;
  sort?: SortMode;
  /** The categories to narrow to; omitted or empty opens unfiltered. */
  categories?: string[];
  search?: string;
  /** Narrows to one merchant group, by the key `groupMerchants` assigns it. */
  merchant?: string;
  recurring?: boolean;
  /** Narrows to rows nobody has confirmed the category of. */
  unverified?: boolean;
  /** One day, `YYYY-MM-DD` — the Overview heatmap's selected day. */
  day?: string;
}

interface StatementState extends Filters {
  period: Period;
  setPeriod: (p: Period) => void;

  setAccountFilter: (ids: string[] | null) => void;
  setWealthAccountFilter: (ids: string[] | null) => void;
  setSpendingCategory: (c: string | null) => void;
  setSearch: (s: string) => void;
  setMerchant: (m: string | null) => void;
  setTxnCats: (c: string[]) => void;
  setTxnTags: (t: string[]) => void;
  setTxnAcctTypes: (t: string[]) => void;
  setRecurring: (r: boolean) => void;
  setUnverified: (u: boolean) => void;
  setDay: (d: string | null) => void;
  setSort: (s: SortMode) => void;
  setMonthsBack: (n: number | ((prev: number) => number)) => void;

  /** Applies a filter set in one update; the Transactions page reads the result. */
  openTransactions: (opts: OpenTransactionsOptions) => void;

  /**
   * The month currently scrolled into view in the transactions list, `YYYY-MM`.
   * Reported by the list rather than chosen, so the period pill can follow the
   * scroll; picking a month explicitly clears it.
   */
  visibleMonth: string | null;
  setVisibleMonth: (m: string | null) => void;

  dark: boolean;
  toggleDark: () => void;

  /** The accent palette in use. Stored per browser, like the theme. */
  accent: Accent;
  setAccent: (a: Accent) => void;

  /** Sidebar rail expanded (236px) or collapsed to icons (68px). Persisted. */
  sideOpen: boolean;
  toggleSide: () => void;

  /** The kill switch for every inline AI suggestion. Stored per browser. */
  showAi: boolean;
  setShowAi: (on: boolean) => void;

  canReset: boolean;
  reset: () => void;
}

const Ctx = createContext<StatementState | null>(null);

export function readStoredTheme(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    if (v === 'dark') return true;
    if (v === 'light') return false;
  } catch {
    /* ignore */
  }
  return false;
}

/** An unset, unreadable or retired key falls back to the default. */
export function readStoredAccent(): Accent {
  if (typeof window === 'undefined') return DEFAULT_ACCENT;
  try {
    const v = window.localStorage.getItem(ACCENT_KEY);
    if (ACCENTS.some((a) => a.key === v)) return v as Accent;
  } catch {
    /* ignore */
  }
  return DEFAULT_ACCENT;
}

/** Collapsed is opt-in: an unset or unreadable preference opens the rail. */
function readStoredSideOpen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) !== 'collapsed';
  } catch {
    /* ignore */
  }
  return true;
}

/** On unless someone turned it off. */
function readStoredShowAi(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(AI_KEY) !== 'off';
  } catch {
    /* ignore */
  }
  return true;
}

export const StatementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [period, setPeriodState] = useState<Period>(currentMonthPeriod);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [visibleMonth, setVisibleMonth] = useState<string | null>(null);
  const [dark, setDark] = useState<boolean>(readStoredTheme);
  const [accent, setAccentState] = useState<Accent>(readStoredAccent);
  const [sideOpen, setSideOpen] = useState<boolean>(readStoredSideOpen);
  const [showAi, setShowAiState] = useState<boolean>(readStoredShowAi);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
    document.body.classList.toggle('st-dark', dark);
  }, [dark]);

  useEffect(() => {
    document.body.classList.add('st-active');
    return () => {
      document.body.classList.remove('st-active', 'st-dark');
    };
  }, []);

  const patch = useCallback((next: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...next }));
  }, []);

  // Changing the period invalidates the day filter and any extra months loaded
  // for the old one.
  const setPeriod = useCallback((p: Period) => {
    setPeriodState(p);
    setVisibleMonth(null);
    setFilters((f) => ({ ...f, day: null, monthsBack: 0 }));
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_KEY, sideOpen ? 'open' : 'collapsed');
    } catch {
      /* ignore */
    }
  }, [sideOpen]);

  const toggleDark = useCallback(() => setDark((d) => !d), []);

  const setAccent = useCallback((next: Accent) => {
    setAccentState(next);
    try {
      window.localStorage.setItem(ACCENT_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);
  const toggleSide = useCallback(() => setSideOpen((o) => !o), []);
  const setShowAi = useCallback((on: boolean) => {
    setShowAiState(on);
    try {
      window.localStorage.setItem(AI_KEY, on ? 'on' : 'off');
    } catch {
      /* ignore */
    }
  }, []);

  const openTransactions = useCallback((opts: OpenTransactionsOptions) => {
    if (opts.period) setPeriodState(opts.period);
    setFilters((f) => ({
      ...f,
      day: opts.day ?? null,
      monthsBack: 0,
      sort: opts.sort ?? 'date',
      search: opts.search ?? '',
      merchant: opts.merchant ?? null,
      recurring: opts.recurring ?? false,
      unverified: opts.unverified ?? false,
      txnCats: opts.categories ?? [],
      txnTags: [],
      txnAcctTypes: [],
    }));
  }, []);

  const defaultPeriod = useMemo(currentMonthPeriod, []);
  const canReset =
    !periodsEqual(period, defaultPeriod) ||
    filters.accountFilter != null ||
    filters.wealthAccountFilter != null ||
    filters.spendingCategory != null ||
    filters.search !== '' ||
    filters.merchant != null ||
    filters.txnCats.length > 0 ||
    filters.txnTags.length > 0 ||
    filters.txnAcctTypes.length > 0 ||
    filters.recurring ||
    filters.unverified ||
    filters.day != null ||
    filters.sort !== 'date' ||
    filters.monthsBack !== 0;

  const reset = useCallback(() => {
    setPeriodState(currentMonthPeriod());
    setFilters(EMPTY_FILTERS);
  }, []);

  const value = useMemo<StatementState>(
    () => ({
      ...filters,
      period,
      setPeriod,
      setAccountFilter: (accountFilter) => patch({ accountFilter }),
      setWealthAccountFilter: (wealthAccountFilter) => patch({ wealthAccountFilter }),
      setSpendingCategory: (spendingCategory) => patch({ spendingCategory }),
      setSearch: (search) => patch({ search }),
      setMerchant: (merchant) => patch({ merchant }),
      setTxnCats: (txnCats) => patch({ txnCats }),
      setTxnTags: (txnTags) => patch({ txnTags }),
      setTxnAcctTypes: (txnAcctTypes) => patch({ txnAcctTypes }),
      setRecurring: (recurring) => patch({ recurring }),
      setUnverified: (unverified) => patch({ unverified }),
      setDay: (day) => patch({ day }),
      setSort: (sort) => patch({ sort }),
      setMonthsBack: (n) =>
        setFilters((f) => ({ ...f, monthsBack: typeof n === 'function' ? n(f.monthsBack) : n })),
      openTransactions,
      visibleMonth,
      setVisibleMonth,
      dark,
      toggleDark,
      accent,
      setAccent,
      sideOpen,
      toggleSide,
      showAi,
      setShowAi,
      canReset,
      reset,
    }),
    [filters, period, setPeriod, patch, openTransactions, visibleMonth, dark, toggleDark, accent, setAccent, sideOpen, toggleSide, showAi, setShowAi, canReset, reset],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useStatement(): StatementState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStatement must be used inside <StatementProvider>');
  return ctx;
}
